import React, {useEffect, useEffectEvent, useImperativeHandle, useRef, useState} from 'react';
import type {Ref} from 'react';

import classnames from 'classnames';

import {addToast, Button, Dialogue, Icon, IconButton, Modal, Text} from '@albert-io/atomic';
import type {GuessModelV1, QuestionModelV3} from '@albert-io/models';
import {submitLegacyGuess} from 'client/components/PracticeView/QuestionActions/submitLegacyGuess';
import sessionStore from 'client/Session/SessionStore';
import {useLatestRef} from 'lib/hooks/useLatestRef';
import {routerHistory} from 'lib/router';

export interface UnsubmittedResponseGuardHandle {
  /** Resolves true when the student may leave the active question, asking first if needed. */
  confirmLeave: () => Promise<boolean>;
  /** Lets the next navigation to `path` through without asking; the caller already asked. */
  allowNavigationTo: (path: string) => void;
  hasUnsubmittedResponse: () => boolean;
}

interface Props {
  question: QuestionModelV3 | null;
  hasActiveGuess: boolean;
  getTimeElapsed: () => number;
  onSubmitted: (guess: GuessModelV1) => void;
  ref?: Ref<UnsubmittedResponseGuardHandle>;
}

function pathnameOf(path: string): string {
  return path.split(/[?#]/)[0];
}

/**
 * Warns a student before they leave a free-response question with a typed response they have not
 * submitted. Outside an assignment nothing saves drafts, so leaving discards the response.
 *
 * Reload and tab close get the browser's native prompt. In-app navigation gets a modal that offers
 * to submit first: router navigation is caught by a blocker, and question switches inside the
 * practice view ask through `confirmLeave` because the view changes question before the URL does.
 */
export default function UnsubmittedResponseGuard({
  question,
  hasActiveGuess,
  getTimeElapsed,
  onSubmitted,
  ref
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const latest = useLatestRef({question, hasActiveGuess, getTimeElapsed, onSubmitted});
  const resolveLeave = useRef<((proceed: boolean) => void) | null>(null);
  const allowedPathname = useRef<string | null>(null);

  function hasUnsubmittedResponse() {
    const {question: activeQuestion, hasActiveGuess: isSubmitted} = latest.current;
    return (
      sessionStore.hasValidSession() &&
      sessionStore.isStudent() &&
      !isSubmitted &&
      activeQuestion?.getQuestionType() === 'free-response' &&
      Boolean(activeQuestion.getStore().hasUserSelectedAnswer)
    );
  }

  function settle(proceed: boolean) {
    resolveLeave.current?.(proceed);
    resolveLeave.current = null;
    setIsOpen(false);
  }

  function confirmLeave() {
    if (!hasUnsubmittedResponse()) {
      return Promise.resolve(true);
    }
    resolveLeave.current?.(false);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveLeave.current = resolve;
    });
  }

  useImperativeHandle(ref, () => ({
    confirmLeave,
    allowNavigationTo: (path: string) => {
      allowedPathname.current = pathnameOf(path);
    },
    hasUnsubmittedResponse
  }));

  const blockRouterNavigation = useEffectEvent(
    async (nextLocation: {pathname: string}): Promise<boolean> => {
      const allowed = allowedPathname.current;
      allowedPathname.current = null;
      if (allowed === nextLocation.pathname) {
        return false;
      }
      // TanStack blocks on a truthy result, so this is inverted: proceed means don't block.
      return !(await confirmLeave());
    }
  );

  const warnBeforeUnload = useEffectEvent((e: BeforeUnloadEvent) => {
    if (hasUnsubmittedResponse()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  const abandonPendingLeave = useEffectEvent(() => settle(false));

  useEffect(() => {
    return routerHistory.block({
      // Reload and tab close are handled by the `beforeunload` listener below.
      enableBeforeUnload: false,
      blockerFn: ({nextLocation}) => blockRouterNavigation(nextLocation)
    });
  }, []);

  useEffect(() => {
    const warnUser = (e: BeforeUnloadEvent) => warnBeforeUnload(e);
    window.addEventListener('beforeunload', warnUser);
    return () => {
      window.removeEventListener('beforeunload', warnUser);
    };
  }, []);

  useEffect(() => () => abandonPendingLeave(), []);

  async function submitAndLeave() {
    const {question: activeQuestion, getTimeElapsed: timeElapsed} = latest.current;
    if (!activeQuestion) {
      settle(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const guess = await submitLegacyGuess({
        question: activeQuestion,
        timeElapsed: timeElapsed()
      });
      latest.current.onSubmitted(guess);
      settle(true);
    } catch {
      addToast({
        color: 'negative',
        title: 'Error',
        message: 'Your response could not be submitted. Please try again.'
      });
      settle(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Modal ariaLabel='Submit and save response?' handleClose={() => settle(false)}>
      {({CloseButtonWrapper, modalContentStyle}) => (
        <Dialogue
          inModal
          hideCloseBtn
          paddingLevel={4}
          className={classnames(modalContentStyle, 'relative rounded-x2! shadow-modal!')}
        >
          <CloseButtonWrapper className='right-x1 top-x1 absolute'>
            <IconButton
              icon='times'
              iconStyle='regular'
              color='secondary'
              size='s'
              label='Close'
              disabled={isSubmitting}
            />
          </CloseButtonWrapper>
          <div className='gap-x5 flex flex-col items-center'>
            <Icon icon={['fal', 'hand']} className='fa-2x content-primary my-x0-half' />
            <Text as='h2' bold size='regular' color='primary' className='mb-x0 text-center'>
              Submit and save response?
            </Text>
            <div className='gap-x1 flex justify-center'>
              <Button
                variant='outlined'
                color='secondary'
                disabled={isSubmitting}
                onClick={() => settle(true)}
                data-testid='unsubmitted-response-modal__leave-btn'
              >
                Leave without saving
              </Button>
              <Button
                disabled={isSubmitting}
                onClick={submitAndLeave}
                data-testid='unsubmitted-response-modal__submit-btn'
              >
                Submit & Exit
              </Button>
            </div>
          </div>
        </Dialogue>
      )}
    </Modal>
  );
}
